import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { DEFAULT_WALL_HEIGHT } from '../../../../features/floorplan3d/geometry';
import { editorStore } from '../../../../store/editorStore';
import { floorplannerStore } from '../../../../store/floorplannerStore';
import { WallType } from '../../../../types/floorplannerTypes';
import { getArcBulge, getDefaultControlPoint } from '../../../../utils/arcUtils';
import DimensionField from '../components/DimensionField';
import MetricsSection from '../components/MetricsSection';
import ToggleRow from '../components/ToggleRow';
import UnitHeader from '../components/UnitHeader';
import type { PropertyPanelController } from '../types';
import {
  panelFieldInputClassName,
  panelGridFieldClassName,
  panelObjectTitleRowClassName,
  toDisplayLength,
  toWorldFromDisplay,
} from '../propertyPanelUtils';

type WallSectionProps = Pick<PropertyPanelController, 'wallLength' | 'setWallLength' | 'localValues' | 'setLocalValues' | 'updateGlobalState' | 'updateWallAttachmentWidths'> & {
  selectedWalls: WallType[];
  displayedWalls: WallType[];
  commonWallLength?: number;
  commonWallWidth?: number;
  commonWallHeight?: number;
};

const WallSection: React.FC<WallSectionProps> = observer(({
  selectedWalls,
  displayedWalls,
  commonWallLength,
  commonWallWidth,
  commonWallHeight,
  wallLength,
  setWallLength,
  localValues,
  setLocalValues,
  updateGlobalState,
  updateWallAttachmentWidths,
}) => {
  const { t } = useTranslation();
  const mixed = t('common.mixed');
  const isMultiWallSelection = selectedWalls.length > 1;
  const hiddenCount = selectedWalls.filter((wall) => editorStore.isWallHidden(wall.id)).length;
  const allSelectedHidden = selectedWalls.length > 0 && hiddenCount === selectedWalls.length;
  const someSelectedHidden = hiddenCount > 0 && !allSelectedHidden;

  return (
    <>
      {displayedWalls.map((wall) => (
        <div key={`wall-${wall?.id}`}>
          <div className={panelObjectTitleRowClassName}>
            <UnitHeader title={selectedWalls.length > 1 ? t('propertyPanel.wallComponents', { count: selectedWalls.length }) : t('propertyPanel.wallComponent')} />
          </div>
          <MetricsSection>
            <div className="grid grid-cols-2 items-end gap-2 py-1">
              <DimensionField
                id="wallLength"
                label={t('propertyPanel.length')}
                fieldClassName={panelGridFieldClassName}
                value={isMultiWallSelection ? (commonWallLength ?? '') : wallLength}
                placeholder={isMultiWallSelection ? mixed : undefined}
                readOnly={isMultiWallSelection}
                inputClassName={`${panelFieldInputClassName} ${isMultiWallSelection ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                onChange={
                  isMultiWallSelection
                    ? undefined
                    : (nextValue) => {
                        let value: number | '';
                        if (isNaN(parseFloat(nextValue)) || parseFloat(nextValue) < 0) {
                          value = '';
                        } else {
                          value = Math.max(1, nextValue ? parseFloat(nextValue) : 1);
                        }
                        setWallLength(value);
                        updateGlobalState(wall?.id, 'length', value);
                      }
                }
                onFocus={isMultiWallSelection ? undefined : (event) => event.target.select()}
                onKeyDown={
                  isMultiWallSelection
                    ? undefined
                    : (event) => {
                        const isZKey = event.key.toLowerCase() === 'z' || event.code === 'KeyZ';
                        const isUndoShortcut = isZKey && !event.shiftKey && !event.altKey &&
                          (event.metaKey || event.ctrlKey);
                        if (editorStore.wallConstructionMode && isUndoShortcut) {
                          event.preventDefault();
                          event.stopPropagation();
                          editorStore.requestWallConstructionUndoLastWall();
                          return;
                        }

                        if (event.key === 'Enter') {
                          updateGlobalState(wall?.id, 'length', wallLength);
                          editorStore.setWallEditingLengthPanel(undefined);
                        }
                      }
                }
                onBeforeInput={
                  isMultiWallSelection
                    ? undefined
                    : (event) => {
                        const inputEvent = event.nativeEvent as InputEvent;
                        if (editorStore.wallConstructionMode && inputEvent.inputType === 'historyUndo') {
                          event.preventDefault();
                          event.stopPropagation();
                          editorStore.requestWallConstructionUndoLastWall();
                        }
                      }
                }
              />
              <DimensionField
                label={t('propertyPanel.width')}
                fieldClassName={panelGridFieldClassName}
                value={isMultiWallSelection ? (commonWallWidth ?? '') : (localValues[wall?.id]?.wallWidth ?? toDisplayLength(wall?.wallWidth || floorplannerStore.wallWidth))}
                placeholder={isMultiWallSelection ? mixed : undefined}
                onChange={(nextValue) => {
                  const value = Math.max(1, nextValue ? parseFloat(nextValue) : 1);
                  setLocalValues((prevValues) => ({
                    ...prevValues,
                    [wall?.id]: {
                      ...prevValues[wall?.id],
                      wallWidth: value,
                    },
                  }));
                  updateGlobalState(wall?.id, 'wallWidth', value);
                  updateWallAttachmentWidths(selectedWalls, value);
                }}
              />
              {editorStore.view3D && (
                <DimensionField
                  label={t('propertyPanel.height')}
                  fieldClassName={panelGridFieldClassName}
                  value={isMultiWallSelection ? (commonWallHeight ?? '') : (localValues[wall?.id]?.wallHeight ?? toDisplayLength(wall?.threeD?.height ?? DEFAULT_WALL_HEIGHT))}
                  placeholder={isMultiWallSelection ? mixed : undefined}
                  onChange={(nextValue) => {
                    const value = Math.max(1, nextValue ? parseFloat(nextValue) : 1);
                    setLocalValues((prevValues) => ({
                      ...prevValues,
                      [wall?.id]: {
                        ...prevValues[wall?.id],
                        wallHeight: value,
                      },
                    }));
                    updateGlobalState(selectedWalls, 'threeDHeight', value);
                  }}
                />
              )}
            </div>
          </MetricsSection>
          <div className="text-sm font-normal p-3">
            {/* Available in 2D as well — hidden walls are easier to select there than in 3D. */}
            <div className="pt-2" title={t('propertyPanel.visibleIn3DTooltip')}>
              <ToggleRow
                label={
                  <span className="inline-flex items-center gap-1.5">
                    <span>{t('propertyPanel.visibleIn3D')}</span>
                    <kbd className="rounded border border-gray-200 bg-white px-1 py-0.5 font-mono text-[10px] leading-none text-gray-500">
                      H
                    </kbd>
                  </span>
                }
                checked={!allSelectedHidden}
                indeterminate={someSelectedHidden}
                onChange={(visible) => {
                  editorStore.setWallsHiddenIn3D(
                    selectedWalls.map((selectedWall) => selectedWall.id),
                    !visible,
                  );
                }}
              />
            </div>
            {!isMultiWallSelection && (
              <div className="flex flex-col gap-2 py-2">
                <div className="flex items-center">
                  <div className="flex flex-row items-center gap-1">
                    <button
                      className={`px-2 py-1.5 text-xs border rounded-sm ${wall?.lineForm !== 'arc' ? 'bg-[#66645F] text-white' : 'bg-white text-gray-700'}`}
                      onClick={() => {
                        if (wall?.lineForm === 'arc') {
                          updateGlobalState(wall?.id, 'lineForm', 'straight');
                          floorplannerStore.setObjectProperty(wall?.id, 'controlPoint', undefined);
                          floorplannerStore.objectNeedsUpdate(wall?.id);
                        }
                      }}
                    >
                      {t('propertyPanel.straight')}
                    </button>
                    <button
                      className={`px-2 py-1.5 text-xs border rounded-sm ${wall?.lineForm === 'arc' ? 'bg-[#66645F] text-white' : 'bg-white text-gray-700'}`}
                      onClick={() => {
                        if (wall?.lineForm !== 'arc') {
                          updateGlobalState(wall?.id, 'lineForm', 'arc');
                          const cp = getDefaultControlPoint(wall.start, wall.end, 0.5);
                          floorplannerStore.setObjectProperty(wall?.id, 'controlPoint', cp);
                          floorplannerStore.objectNeedsUpdate(wall?.id);
                        }
                      }}
                    >
                      {t('propertyPanel.arc')}
                    </button>
                  </div>
                </div>
                {wall?.lineForm === 'arc' && wall?.controlPoint && (
                  <div className="flex flex-row items-center justify-start">
                    <DimensionField
                      label={t('propertyPanel.bulge')}
                      value={Math.round(toDisplayLength(getArcBulge(wall)))}
                      onChange={(nextValue) => {
                        const bulgeInDisplay = parseFloat(nextValue) || 0;
                        const bulgeInWorld = toWorldFromDisplay(bulgeInDisplay);
                        const cp = getDefaultControlPoint(wall.start, wall.end, bulgeInWorld);
                        floorplannerStore.setObjectProperty(wall?.id, 'controlPoint', cp);
                        floorplannerStore.objectNeedsUpdate(wall?.id);
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="border-b border-gray-300" />
        </div>
      ))}
    </>
  );
});

export default WallSection;
